import React, { useEffect, useState } from 'react';
import { Card, CardBody, Col, Form, FormGroup, Input, Label, Row } from 'reactstrap';
import NumberFormat from 'react-number-format';
import { toast } from "react-toastify";
import Select from 'react-select';
import classNames from 'classnames';
import _ from 'lodash';
import { ItemTypeahead, SpinnerButton } from './';
import { DesktopContainer } from '../layouts';
import { api, constants, dateHelpers, helpers } from '../utils';

const BLANK_DEPT = {
   "deptId": "",
   "deptName": "",
   "defaultLabor": undefined
}

export default function BOMGlobalUpdate() {
   const [componentDtos, setComponentDtos] = useState(null);
   const [department, setDepartment] = useState(null);
   const [params, setParams] = useState({
      machineSetupType: { label: "All", value: -1 },
      itemClasses: [],
      wasteItem: null,
      wastePercent: undefined,
      deptLabor: null
   })
   const [refData, setRefData] = useState({});
   const [wasteSummaryLabel, setWasteSummaryLabel] = useState(null);
   const [working, setWorking] = useState(false);
   const [workingAction, setWorkingAction] = useState(null);
   document.title = `BoM Global Updates`;

   useEffect(loadRefData, [])

   function deleteDepartment() {
      setWorking(true);
      setWorkingAction("deleteDepartment");
      api
         .fetch(`BOM/DeleteDepartmentLabor/${department.deptId}`)
         .then(r => {
            if (r.data.success) {
               toast.success("Department deleted!");
               loadRefData();
               setParams({
                  ...params,
                  deptLabor: null
               });
               setDepartment(null);
            } else {
               toast.error(r.data.message);
            }
         })
         .catch(api.catchHandler)
         .finally(() => {
            setWorkingAction(null);
            setWorking(false);
         })
   }

   function getDepartmentValue(field) {
      if (department === null) return "";
      if (department[field] === undefined) return "";
      return department[field];
   }

   function getLaborCostReport() {
      setWorking(true);
      setWorkingAction("laborCostReport");
      toast.warning("Generating report. This can take a few minutes. Please be patient.");
      api
         .postGetBlob("Report/LaborCostReport")
         .then(r => {
            if (r.data) {
               const fileName = `Item_Labor_Cost_Rpt_${dateHelpers.toMDYDateString(Date.now())}.xlsx`
               helpers.downloadExport(r.data, fileName, constants.MIME_TYPES.xlsx);
            } else {
               toast.error(r.message || "Something went wrong generating the report");
            }
         })
         .catch(api.catchHandler)
         .finally(() => {
            setWorking(false);
            setWorkingAction(null);
         });
   }

   function getLaborCostMSRReport() {
      setWorking(true);
      setWorkingAction("laborCostMSRReport");
      toast.warning("Generating report. This can take a few minutes. Please be patient.")
      api
         .postGetBlob(`Report/LaborCostMSRReport`, { name: params.machineSetupType.label })
         .then(r => {
            if (r.data) {
               const fileName = `Item_Labor_Cost_MSR_Rpt_${params.machineSetupType.label}_${dateHelpers.toMDYDateString(Date.now())}.xlsx`
               helpers.downloadExport(r.data, fileName, constants.MIME_TYPES.xlsx);
            } else {
               toast.error(r.message || "Something went wrong generating the report");
            }
         })
         .catch(api.catchHandler)
         .finally(() => {
            setWorking(false);
            setWorkingAction(null);
         });
   }

   function handleDepartmentEdit(field, value) {
      if (!department) return;
      const deptCopy = _.cloneDeep(department);
      deptCopy[field] = value;
      setDepartment(deptCopy);
   }

   function handleDepartmentSelect(opt) {
      setParams({
         ...params,
         deptLabor: opt
      });
      if (opt === null) {
         setDepartment(null);
         return;
      }
      if (opt.value === "___") {
         setDepartment(_.assign({}, BLANK_DEPT));
         return;
      }
      setDepartment(_.find(refData.deptsLabor, dl => dl.deptId === opt.value));
   }

   function handleWasteItemChange(opt) {
      setParams({
         ...params,
         wasteItem: opt,
         wastePercent: undefined
      })
      if (opt === null) {
         setComponentDtos(null);
         setWasteSummaryLabel(null);
         return;
      }
      api
         .fetch(`BOM/GetBOMsByComponent/${opt.itemNumber}`)
         .then(r => {
            if (r.data.success) {
               const wasteDtos = r.data.message;
               if (wasteDtos.length) {
                  let wasteSummaryLabel = `Default Waste = ${wasteDtos[0].defaultWaste} %`;
                  if (_.some(wasteDtos, dto => dto.defaultWaste !== dto.componentWaste)) {
                     const minWaste = _.minBy(wasteDtos, dto => dto.componentWaste).componentWaste;
                     const maxWaste = _.maxBy(wasteDtos, dto => dto.componentWaste).componentWaste;
                     wasteSummaryLabel = minWaste === maxWaste
                        ? `Custom Waste = ${wasteDtos[0].componentWaste} %`
                        : "Various Waste %s Exist";
                  }
                  setWasteSummaryLabel(wasteSummaryLabel);
                  setComponentDtos(wasteDtos);
               } else {
                  setWasteSummaryLabel(null);
                  setComponentDtos([]);
               }
            } else {
               toast.error(r.data.message);
            }
         })
         .catch(api.catchHandler)
   }

   function loadRefData() {
      api
         .fetch("BOM/LoadBOMGlobalRefData")
         .then(r => {
            _.forEach(r.data.deptsLabor, dl => {
               dl.label = dl.displayName;
               dl.value = dl.deptId;
            });
            setRefData(r.data);
         })
         .catch(api.catchHandler)
   }

   function saveDepartment() {
      setWorking(true);
      setWorkingAction("saveDepartment");
      const payload = {
         departmentId: department.deptId,
         departmentName: department.deptName,
         departmentLabor: department.defaultLabor,
      }
      api
         .post(`BOM/SaveDepartmentLabor`, payload)
         .then(r => {
            if (r.data.success) {
               toast.success("Department saved!")
               loadRefData();
               setParams({
                  ...params,
                  deptLabor: null
               });
               setDepartment(null);
            } else {
               toast.error(r.data.message);
            }
         })
         .catch(api.catchHandler)
         .finally(() => {
            setWorkingAction(null);
            setWorking(false);
         })
   }

   function updateCosts(actionName, isMsr = false) {
      setWorking(true);
      setWorkingAction(actionName);
      toast.warning("Updating costs. This can take a few minutes. Please be patient.")
      const payload = {
         isMsr: isMsr,
         machineSetupName: params.machineSetupType.label,
         classList: isMsr ? [] : _.map(params.itemClasses, cls => cls.value)
      }
      api
         .post(`BOM/UpdateGlobalCosts`, payload)
         .then(r => {
            r.data.success
               ? toast.success(r.data.message)
               : toast.error(r.data.message);
         })
         .catch(api.catchHandler)
         .finally(() => {
            setWorking(false);
            setWorkingAction(null);
         })
   }

   function updateWaste() {
      setWorking(true);
      setWorkingAction("updateWaste");
      const payload = {
         wasteItemNumber: params.wasteItem.value,
         wastePercent: params.wastePercent
      };
      api
         .post(`BOM/UpdateWaste`, payload)
         .then(r => {
            if (r.data.success) {
               toast.success("Waste updated!");
               handleWasteItemChange(params.wasteItem);
            } else {
               toast.error(r.data.message);
            }
         })
         .catch(api.catchHandler)
         .finally(() => {
            setWorking(false);
            setWorkingAction(null);
         })
   }

   function validateDepartmentForm() {
      if (department === null) return true;
      if (department.deptId.length === 0) return true;
      if (department.deptName.length === 0) return true;
      if (department.defaultLabor === undefined) return true;
      return false;
   }

   return (
      <DesktopContainer screenName="Global Updates">
         <div className='filter-header'>
            <Row className="mx-3">
               <Col xs={12}>
                  <h5 children="BOM Cost Global Update" className="text-white" />
               </Col>
            </Row>
         </div>
         <Card className="dashboard-card">
            <CardBody className='pt-0'>
               <hr />
               <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className="textAlign-left">To see how items will change if global updates are allowed on the item, view the report. <br />Any rows highlighed in yellow have calculation errors. See column S for details.</div>
                  <SpinnerButton
                     className="shredButton"
                     style={{ width: "15rem" }}
                     onClick={getLaborCostReport}
                     on={workingAction === "laborCostReport"}
                     disabled={working}
                  >
                     All Items Detail Report
                  </SpinnerButton>
               </div>
               <div className="d-flex justify-content-between align-items-center mb-3">
                  <Label className="fw-bold">Machine Type:</Label>
                  <Select
                     classNamePrefix='serviceThreadSelect'
                     className="w-50"
                     options={refData.machineSetupTypes}
                     value={params.machineSetupType}
                     onChange={opt => setParams({
                        ...params,
                        machineSetupType: opt
                     })}
                     isLoading={refData === {}}
                  />
                  <div>
                     <SpinnerButton
                        className="shredButton me-2"
                        style={{ width: "9rem" }}
                        onClick={getLaborCostMSRReport}
                        on={workingAction === "laborCostMSRReport"}
                        disabled={working}
                     >
                        MSR Report
                     </SpinnerButton>
                     <SpinnerButton
                        className="shredButton"
                        style={{ width: "15rem" }}
                        onClick={() => {
                           if (params.machineSetupType.value === -1) {
                              toast.warning("No machine setup was selected. Select an MSR and try again.");
                              return;
                           }
                           updateCosts("updateMSRCosts", true);
                        }}
                        on={workingAction === "updateMSRCosts"}
                        disabled={working}
                     >
                        Update Costs for MSR
                     </SpinnerButton>
                  </div>
               </div>
               <hr />
               <div className="text-center">
                  If no class(es) are selected then all items will be updated if their global update flag allows it
               </div>
               <div className="d-flex align-items-center justify-content-between">
                  <Label className="text-center me-2">
                     <span className="fw-bold">Class(es):</span><br />(Optional)
                  </Label>
                  <Select
                     classNamePrefix='serviceThreadSelect'
                     className="w-75"
                     options={refData.itemClasses}
                     value={params.itemClasses}
                     onChange={opt => setParams({
                        ...params,
                        itemClasses: opt
                     })}
                     isLoading={refData === {}}
                     isMulti
                  />
                  <SpinnerButton
                     className="shredButton"
                     style={{ width: "10rem" }}
                     onClick={() => updateCosts("updateCosts")}
                     on={workingAction === "updateCosts"}
                     disabled={working}
                  >
                     Update Costs
                  </SpinnerButton>
               </div>
            </CardBody>
         </Card>
         <div className='filter-header'>
            <Row className="mx-3">
               <Col xs={12}>
                  <h5 children="Update Component Waste Across BOMs" className="text-white" />
               </Col>
            </Row>
         </div>
         <Card className="dashboard-card">
            <CardBody className='pt-0'>
               <hr />
               <Row className="mb-3">
                  <Label xs={3} className="text-end">
                     <span className="fw-bold">Item:</span>
                  </Label>
                  <Col xs={8}>
                     <ItemTypeahead value={params.wasteItem} onChange={handleWasteItemChange} />
                  </Col>
               </Row>
               <Row className="mb-3">
                  <Label xs={3} className="text-end">
                     <span className="fw-bold">Waste %:</span>
                  </Label>
                  <Col xs={7}>
                     <NumberFormat
                        className={classNames({
                           "form-control me-2": true,
                           "readonly-input fw-bold": params.wasteItem === null
                        })}
                        value={params.wastePercent === undefined ? "" : params.wastePercent}
                        onValueChange={({ floatValue }) => {
                           if (floatValue !== undefined) {
                              if (floatValue < 0) floatValue = 0;
                              if (floatValue > 100) floatValue = 100
                           }
                           setParams({
                              ...params,
                              wastePercent: floatValue
                           })
                        }}
                        allowNegative={false}
                        readOnly={params.wasteItem === null}
                     />
                  </Col>
                  <Col xs={1}>
                     <SpinnerButton
                        className="shredButton"
                        on={workingAction === "updateWaste"}
                        onClick={updateWaste}
                        disabled={working
                           || params.wasteItem === null
                           || params.wastePercent === undefined
                           || componentDtos === null
                           || componentDtos.length === 0
                        }
                     >
                        Update
                     </SpinnerButton>
                  </Col>
               </Row>
               <Row>
                  <Col className="text-center">
                     {componentDtos !== null &&
                        <>
                           <span>
                              {componentDtos.length === 0
                                 ? "There are currently no Bills of Material containing that component item."
                                 : `There are currently ${componentDtos.length} Bills of Material containing the component item that will be updated.`
                              }
                           </span>
                           <br />
                           <span>{wasteSummaryLabel}</span>
                        </>
                     }
                  </Col>
               </Row>
            </CardBody>
         </Card >
         <div className='filter-header'>
            <Row className="mx-3">
               <Col xs={12}>
                  <h5 children="Department Maintenance" className="text-white" />
               </Col>
            </Row>
         </div>
         <Card className="dashboard-card">
            <CardBody className='pt-0'>
               <hr />
               <Form>
                  <FormGroup row>
                     <Label xs={3} className="text-end">
                        <span className="fw-bold">Department:</span>
                     </Label>
                     <Col xs={8}>
                        <Select
                           classNamePrefix='serviceThreadSelect'
                           options={refData.deptsLabor}
                           value={params.deptLabor}
                           onChange={handleDepartmentSelect}
                           menuPlacement="auto"
                           isLoading={refData === {}}
                           isClearable
                        />
                     </Col>
                  </FormGroup>
                  <FormGroup row>
                     <Label xs={3} className="text-end">
                        <span className="fw-bold">Department Acct:</span>
                     </Label>
                     <Col xs={8}>
                        <Input
                           className={classNames({"readonly-input fw-bold": department === null || params.deptLabor?.deptId !== "___"})}
                           value={getDepartmentValue("deptId")}
                           onChange={e => handleDepartmentEdit("deptId", e.target.value)}
                           readOnly={ department === null || params.deptLabor?.deptId !== "___" }
                        />
                     </Col>
                  </FormGroup>
                  <FormGroup row>
                     <Label xs={3} className="text-end">
                        <span className="fw-bold">Department Name:</span>
                     </Label>
                     <Col xs={8}>
                        <Input
                           className={classNames({"readonly-input fw-bold": department === null})}
                           value={getDepartmentValue("deptName")}
                           onChange={e => handleDepartmentEdit("deptName", e.target.value)}
                           readOnly={department === null}
                        />
                     </Col>
                  </FormGroup>
                  <FormGroup row>
                     <Label xs={3} className="text-end">
                        <span className="fw-bold">Default Labor Rate:</span>
                     </Label>
                     <Col xs={8}>
                        <NumberFormat
                           className={classNames({"form-control": true, "readonly-input fw-bold": department === null})}
                           value={getDepartmentValue("defaultLabor")}
                           onValueChange={({ floatValue }) => {
                              let valueUpdate = floatValue;
                              if (valueUpdate !== undefined) {
                                 if (valueUpdate < 0) valueUpdate = 0;
                                 if (valueUpdate > 999.9999) valueUpdate = 999.9999
                              }
                              handleDepartmentEdit("defaultLabor", valueUpdate)
                           }}
                           decimalScale={4}
                           allowNegative={false}
                           readOnly={department === null}
                        />
                     </Col>
                  </FormGroup>
               </Form>
               <Row>
                  <Col className="text-end">
                     <SpinnerButton
                        className="shredButton"
                        style={{ width: "7rem" }}
                        onClick={saveDepartment}
                        on={workingAction === "saveDepartment"}
                        disabled={working || validateDepartmentForm()}
                     >
                        Save
                     </SpinnerButton>
                  </Col>
                  <Col>
                     <SpinnerButton
                        className="shredButton"
                        style={{ width: "7rem" }}
                        onClick={deleteDepartment}
                        on={workingAction === "deleteDepartment"}
                        disabled={working || validateDepartmentForm()}
                     >
                        Delete
                     </SpinnerButton>
                  </Col>
               </Row>
            </CardBody>
         </Card>

      </DesktopContainer >
   )
}